import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { ToastContext } from '../../context/ToastContext';
import { fetchObituaries } from '../../utils/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { ListSkeleton } from '../../components/common/Skeleton';
import { FileText, Eye, Edit2, Trash2, Share2 } from 'lucide-react';

const STATUS_FILTERS = [
  { value: 'all', label: '전체' },
  { value: 'active', label: '진행중' },
  { value: 'ended', label: '종료' },
];

// 발인일 기준 진행상태 / D-day 계산
const getDerivedStatus = (departureDate) => {
  if (!departureDate) return 'active';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dep = new Date(departureDate);
  dep.setHours(0, 0, 0, 0);
  return dep < today ? 'ended' : 'active';
};

const getDday = (departureDate) => {
  if (!departureDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dep = new Date(departureDate);
  dep.setHours(0, 0, 0, 0);
  const diff = Math.round((dep - today) / (1000 * 60 * 60 * 24));
  if (diff > 0) return `D-${diff}`;
  if (diff === 0) return 'D-DAY';
  return '발인 완료';
};

const MyObituariesPage = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const [obituaries, setObituaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadObituaries = useCallback(async () => {
    try {
      setLoading(true);
      setIsError(false);
      const data = await fetchObituaries();
      const userObituaries = data.filter((obit) => obit.userId === user?.id);
      setObituaries(userObituaries);
    } catch (error) {
      setIsError(true);
      showToast('부고 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [user, showToast]);

  useEffect(() => {
    loadObituaries();
  }, [loadObituaries]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return obituaries;
    return obituaries.filter(
      (o) => (o.status || getDerivedStatus(o.departureDate)) === statusFilter
    );
  }, [obituaries, statusFilter]);

  const handleView = (id) => navigate(`/obituaries/${id}`);
  const handleEdit = (id) => navigate(`/obituaries/${id}/edit`);
  const handleShare = (id) => navigate(`/obituaries/${id}/share`);

  const confirmDelete = async () => {
    if (deleteTarget == null) return;
    setDeleting(true);
    await new Promise((resolve) => setTimeout(resolve, 400)); // mock 삭제 지연
    setObituaries((prev) => prev.filter((obit) => obit.id !== deleteTarget));
    setDeleting(false);
    setDeleteTarget(null);
    showToast('부고가 삭제되었습니다.', 'success');
  };

  const getStatusBadge = (obituary) => {
    const status = obituary.status || getDerivedStatus(obituary.departureDate);
    const statusConfig = {
      active: { label: '진행중', className: 'bg-green-100 text-green-800' },
      ended: { label: '종료', className: 'bg-gray-100 text-gray-800' },
      pending: { label: '대기', className: 'bg-yellow-100 text-yellow-800' },
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${config.className}`}
      >
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6">
          <h2 className="mb-6 text-xl font-bold text-primary">내 부고 관리</h2>
          <ListSkeleton rows={3} />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="p-6">
          <ErrorState
            title="부고 목록을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 후 다시 시도해주세요."
            onRetry={loadObituaries}
          />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-primary">내 부고 관리</h2>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/obituaries/create')}
          >
            부고 작성하기
          </Button>
        </div>

        {/* 상태 필터 */}
        {obituaries.length > 0 && (
          <div className="mb-4 flex gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                aria-pressed={statusFilter === f.value}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                  statusFilter === f.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {obituaries.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-accent-300 mb-4" />
            <p className="text-accent-600 mb-4">작성한 부고가 없습니다.</p>
            <Button
              variant="outline"
              onClick={() => navigate('/obituaries/create')}
            >
              부고 작성하기
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="rounded-lg bg-accent-50 px-4 py-10 text-center text-sm text-accent-600">
            해당 상태의 부고가 없습니다.
          </p>
        ) : (
          <div className="space-y-4">
            {filtered.map((obituary) => {
              const status =
                obituary.status || getDerivedStatus(obituary.departureDate);
              const dday = getDday(obituary.departureDate);
              return (
                <div
                  key={obituary.id}
                  className="border border-accent-200 rounded-lg p-4 hover:border-primary transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-primary">
                          {obituary.deceasedName}
                        </h3>
                        {getStatusBadge(obituary)}
                        {dday && status === 'active' && (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-bold text-primary-700">
                            {dday}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-accent-600">
                        {obituary.funeralHome}
                        {obituary.funeralRoom ? ` | ${obituary.funeralRoom}` : ''}
                      </p>
                      <p className="text-sm text-accent-600">
                        발인:{' '}
                        {obituary.departureDate
                          ? new Date(obituary.departureDate).toLocaleDateString(
                              'ko-KR'
                            )
                          : '-'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-3 border-t border-accent-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(obituary.id)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      보기
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(obituary.id)}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      수정
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShare(obituary.id)}
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      공유
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(obituary.id)}
                      className="text-red-600 hover:bg-red-50 border-red-300"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      삭제
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteTarget != null}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="부고 삭제"
        message="이 부고를 삭제하시겠습니까? 삭제된 부고는 복구할 수 없습니다."
        confirmLabel="삭제"
        confirmVariant="danger"
        loading={deleting}
      />
    </Card>
  );
};

export default MyObituariesPage;
